import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormField } from '@/components/shared/form-field';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Listbox } from '@/components/ui/listbox';
import { toast } from '@/components/ui/toast';
import {
  isElevationCancelled,
  useElevateSession,
} from '@/hooks/use-elevate-session';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { formatMoney } from '@/utils/format';

interface SmsConnection {
  id: string | null;
  mode: 'byok' | 'trinity';
  status: 'draft' | 'active' | 'suspended';
  senderId: string | null;
  maskedHint: string | null;
  isConfigured: boolean;
  hasChurchCredentials: boolean;
}

interface CreditsSummary {
  balanceMinor: number;
  currency: string;
  costPerSegmentMinor: number;
  segmentLength: number;
}

interface SmsSendingOptionsCardProps {
  hubtelReady: boolean;
  onBuyCredits?: () => void;
}

export const SmsSendingOptionsCard = ({ hubtelReady, onBuyCredits }: SmsSendingOptionsCardProps) => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const { elevateAndRetry, dialog: sessionDialogs } = useElevateSession();

  const [mode, setMode] = useState<'byok' | 'trinity'>('byok');

  const connection = useQuery({
    queryKey: ['messaging', 'sms-connection'],
    queryFn: () => api.get<SmsConnection>('/messaging/sms-connection'),
  });

  const credits = useQuery({
    queryKey: ['messaging', 'credits'],
    queryFn: () => api.get<CreditsSummary>('/messaging/credits'),
  });

  const segmentsAffordable =
    credits.data && credits.data.costPerSegmentMinor > 0
      ? Math.floor(credits.data.balanceMinor / credits.data.costPerSegmentMinor)
      : 0;

  useEffect(() => {
    setMode(connection.data?.mode ?? 'byok');
  }, [connection.data?.mode]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['messaging', 'sms-connection'] });
    void queryClient.invalidateQueries({ queryKey: ['messaging', 'credits'] });
    void queryClient.invalidateQueries({ queryKey: ['integrations', 'hubtel'] });
  };

  const saveHubtel = useMutation({
    mutationFn: () =>
      elevateAndRetry(() => api.post('/messaging/sms-connection/hubtel', {}), {
        description: t('hubtel.sending.elevateDescription'),
      }),
    onSuccess: () => {
      toast.success(t('hubtel.sending.saved'));
      invalidate();
    },
    onError: (err) => {
      if (isElevationCancelled(err)) return;
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const saveTrinity = useMutation({
    mutationFn: () =>
      elevateAndRetry(() => api.post('/messaging/sms-connection/trinity', {}), {
        description: t('hubtel.sending.elevateDescription'),
      }),
    onSuccess: () => {
      toast.success(t('hubtel.sending.saved'));
      invalidate();
    },
    onError: (err) => {
      if (isElevationCancelled(err)) return;
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const status = connection.data?.status ?? 'draft';
  const statusLabel =
    status === 'active'
      ? t('hubtel.sending.statusActive')
      : status === 'suspended'
        ? t('hubtel.sending.statusSuspended')
        : t('hubtel.sending.statusDraft');

  const isConfigured = connection.data?.isConfigured === true;
  const modeDirty = mode !== (connection.data?.mode ?? 'byok');

  return (
    <>
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <CardTitle>{t('hubtel.sending.title')}</CardTitle>
              <CardDescription>{t('hubtel.sending.description')}</CardDescription>
            </div>
            {isConfigured ? (
              <Badge
                variant={
                  status === 'active' ? 'success' : status === 'suspended' ? 'warning' : 'neutral'
                }
                className="shrink-0"
              >
                {statusLabel}
              </Badge>
            ) : null}
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {!isConfigured ? (
            <p className="text-body-sm text-fg-secondary">{t('hubtel.sending.empty')}</p>
          ) : null}

          <FormField
            label={t('hubtel.sending.mode')}
            hint={
              mode === 'byok' ? t('hubtel.sending.modeByokHelp') : t('hubtel.sending.modeTrinityHelp')
            }
          >
            {(field) => (
              <Listbox
                {...field}
                value={mode}
                onChange={(value) => setMode((value as 'byok' | 'trinity') ?? 'byok')}
                options={[
                  { value: 'trinity', label: t('hubtel.sending.modeTrinity') },
                  { value: 'byok', label: t('hubtel.sending.modeByok') },
                ]}
              />
            )}
          </FormField>

          {mode === 'byok' ? (
            hubtelReady ? (
              <p className="text-body-sm text-fg-secondary">
                {connection.data?.senderId
                  ? `${t('hubtel.senderIdOnFile', { senderId: connection.data.senderId })} · ${t('hubtel.sending.hubtelReady')}`
                  : t('hubtel.sending.hubtelReady')}
              </p>
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3">
                <p className="text-body-sm text-fg-primary">{t('hubtel.sending.hubtelRequired')}</p>
                <Button variant="secondary" size="sm" asChild>
                  <a href="#sms-keys">{t('hubtel.sending.connectKeys')}</a>
                </Button>
              </div>
            )
          ) : credits.data ? (
            <div className="rounded-md border border-brand/20 bg-brand/5 p-4">
              <p className="text-caption font-medium uppercase tracking-wide text-fg-secondary">
                {t('hubtel.sending.creditsTitle')}
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-fg">
                {formatMoney(credits.data.balanceMinor, credits.data.currency)}
              </p>
              <p className="mt-1 text-body-sm text-fg-secondary">
                {t('hubtel.sending.creditsSegmentsEstimate', {
                  count: segmentsAffordable,
                  length: credits.data.segmentLength,
                })}
              </p>
              <div className="mt-3 flex justify-end">
                {onBuyCredits ? (
                  <Button variant="secondary" size="sm" onClick={onBuyCredits}>
                    {t('hubtel.sending.buyCredits')}
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}
        </CardContent>

        {modeDirty ? (
          <CardFooter>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="secondary" onClick={() => setMode(connection.data?.mode ?? 'byok')}>
                {tCommon('action.cancel')}
              </Button>
              {mode === 'trinity' && onBuyCredits ? (
                <Button variant="secondary" onClick={onBuyCredits}>
                  {t('hubtel.sending.buyCredits')}
                </Button>
              ) : null}
              <Button
                disabled={
                  mode === 'byok' ? !hubtelReady || saveHubtel.isPending : saveTrinity.isPending
                }
                loading={mode === 'byok' ? saveHubtel.isPending : saveTrinity.isPending}
                onClick={() => (mode === 'byok' ? saveHubtel.mutate() : saveTrinity.mutate())}
              >
                {mode === 'byok' ? t('hubtel.sending.saveByok') : t('hubtel.sending.saveTrinity')}
              </Button>
            </div>
          </CardFooter>
        ) : null}
      </Card>

      {sessionDialogs}
    </>
  );
};
